import { Box, Button } from '@chakra-ui/react'
import { useProductsAndCategories } from 'hooks/useProductsAndCategories'
import { useStorageUnitOrders } from 'hooks/useStorageUnitOrders'
import { useStorageUnitSubscriptions } from 'hooks/useStorageUnitSubscriptions'
import type { PriceWrapper } from 'interfaces'
import { indexBy, prop } from 'ramda'
import React from 'react'
import { useIntl } from 'react-intl'
import { StorageUnitSubscriptionInfo } from 'routes/StorageUnitFlow/components/StorageUnitSubscriptionInfo'
import { add, sanitizeAmount, toSupportedCurrency } from 'utils'
import { groupStorageUnitSubscriptionsByWarehouse } from './storageUnitGroups'

export const StorageUnitSubscription = ({ mb }: { mb?: number }) => {
  const { formatMessage } = useIntl()
  const storageUnitSubscription = useStorageUnitSubscriptions()
  const { userProducts, isLoading: productsLoading } = useProductsAndCategories({
    enabled: Boolean(storageUnitSubscription.hasStorageUnitSubscriptions),
  })
  const { data: storageUnitOrders, isInitialLoading: storageUnitOrdersLoading } =
    useStorageUnitOrders()

  if (!storageUnitSubscription.hasStorageUnitSubscriptions) {
    return null
  }

  const isLoading = productsLoading || storageUnitOrdersLoading

  // Subscriptions are already narrowed to storage units, so the products need no filtering
  const productIndex = indexBy(prop('id'), userProducts ?? [])
  const productSubscriptionPrices: PriceWrapper[] =
    storageUnitSubscription.data?.map((productSubscription) => ({
      amount: productSubscription.payableAmount,
      currency: toSupportedCurrency(productSubscription.payableAmountCurrency),
    })) ?? []

  const totalPrice = add(...productSubscriptionPrices)

  const storageUnitGroups = groupStorageUnitSubscriptionsByWarehouse(
    storageUnitSubscription.data ?? [],
    storageUnitOrders?.items ?? []
  ).map(({ warehouse, storageUnits }) => ({
    id: warehouse?.id ?? 'unknown-warehouse',
    title: warehouse?.name,
    subtitle: warehouse
      ? `${warehouse.address.street}, ${warehouse.address.postalCode}`
      : undefined,
    storageUnits: storageUnits.flatMap(({ subscription, locationLabel }) => {
      const product = productIndex[subscription.product.id]
      // Keep the row while products load so the size skeleton has somewhere to render
      if (!product && !productsLoading) {
        return []
      }
      return {
        id: subscription.id,
        sizeProps: {
          size: product?.metadata?.size ?? 0,
          unit: (
            <>
              m <sup>2</sup>
            </>
          ),
        },
        description: locationLabel,
      }
    }),
  }))

  return (
    <Box mb={mb}>
      <StorageUnitSubscriptionInfo
        isLoadingUsage={isLoading}
        title={formatMessage({ id: 'word.your.subscriptions' })}
        storageUnitGroups={storageUnitGroups}
        priceText={formatMessage({ id: 'word.storing.price' })}
        totalPrice={`${sanitizeAmount(totalPrice.amount)} / ${formatMessage({
          id: 'word.monthAbbreviation',
        })}`}
        cta={
          <Button
            as="a"
            href="mailto:hello@wanda.space"
            width="100%"
            variant="solid"
            colorScheme="ctaBlack"
          >
            {formatMessage({ id: 'space.orderPopup.delivery' })}
          </Button>
        }
      />
    </Box>
  )
}
